import type { SurveyFull } from 'src/redux/survey/survey.types';
import { SurveyQuestionType as QType } from 'src/redux/survey/survey.types';
import type { SurveyGroupQuestionClusterType } from 'src/types/survey-group-api.types';
import type { SurveyGroup } from 'src/types/survey-group.types';
import type { AppDispatch } from 'src/redux/redux-store';
import { surveyGroupsEndpoints } from 'src/redux/survey/survey-groups.endpoints';

function toClusterQuestionType(type: string | undefined): SurveyGroupQuestionClusterType | null {
  if (type === QType.SingleSelect) {
    return 'single_select';
  }
  if (type === QType.MultiSelect) {
    return 'multi_select';
  }
  return null;
}

/**
 * Creates the group on the server, then question clusters, question mappings,
 * answer clusters, and answer mappings to match the combine wizard model.
 */
export async function persistSurveyGroupToApi(
  dispatch: AppDispatch,
  params: { group: SurveyGroup; anchorSurvey: SurveyFull }
): Promise<void> {
  const { group, anchorSurvey } = params;

  const created = await dispatch(
    surveyGroupsEndpoints.endpoints.createSurveyGroup.initiate({
      name: group.name,
      surveys: [group.anchorExtSurveyId, ...group.memberExtSurveyIds],
    })
  ).unwrap();

  const groupId = created.id;
  const questionClusterByAnchorQuestionId = new Map<string, string>();

  for (const aq of anchorSurvey.questions) {
    const clusterType = toClusterQuestionType(aq.type);
    if (!clusterType) {
      continue;
    }
    const cluster = await dispatch(
      surveyGroupsEndpoints.endpoints.createQuestionCluster.initiate({
        groupId,
        body: {
          type: clusterType,
          default_title_override: aq.title,
        },
      })
    ).unwrap();

    questionClusterByAnchorQuestionId.set(aq.id, cluster.id);

    await dispatch(
      surveyGroupsEndpoints.endpoints.createQuestionMapping.initiate({
        groupId,
        body: {
          cluster: cluster.id,
          ext_survey_id: group.anchorExtSurveyId,
          survey_question_id: aq.id,
        },
      })
    ).unwrap();

    for (const m of group.mappings) {
      const lq = m.linkedQuestions.find((x) => x.anchorQuestionId === aq.id);
      if (!lq?.memberQuestionId) {
        continue;
      }
      await dispatch(
        surveyGroupsEndpoints.endpoints.createQuestionMapping.initiate({
          groupId,
          body: {
            cluster: cluster.id,
            ext_survey_id: m.extSurveyId,
            survey_question_id: lq.memberQuestionId,
          },
        })
      ).unwrap();
    }
  }

  for (const aq of anchorSurvey.questions) {
    const qClusterId = questionClusterByAnchorQuestionId.get(aq.id);
    if (!qClusterId) {
      continue;
    }

    for (const aa of aq.answers) {
      const ac = await dispatch(
        surveyGroupsEndpoints.endpoints.createAnswerCluster.initiate({
          groupId,
          body: {
            question_cluster: qClusterId,
            default_title_override: aa.title,
          },
        })
      ).unwrap();

      const answerClusterId = ac.id;

      await dispatch(
        surveyGroupsEndpoints.endpoints.createAnswerMapping.initiate({
          groupId,
          body: {
            cluster: answerClusterId,
            ext_survey_id: group.anchorExtSurveyId,
            survey_question_answer_id: aa.id,
          },
        })
      ).unwrap();

      for (const m of group.mappings) {
        const lq = m.linkedQuestions.find((x) => x.anchorQuestionId === aq.id);
        if (!lq?.memberQuestionId) {
          continue;
        }
        const bucket = lq.answerBuckets.find((b) => b.anchorAnswerId === aa.id);
        if (!bucket?.memberAnswerId) {
          continue;
        }
        await dispatch(
          surveyGroupsEndpoints.endpoints.createAnswerMapping.initiate({
            groupId,
            body: {
              cluster: answerClusterId,
              ext_survey_id: m.extSurveyId,
              survey_question_answer_id: bucket.memberAnswerId,
            },
          })
        ).unwrap();
      }
    }
  }
}
